import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { deleteArticle, getArticles } from "../../redux/slices/articleSlice";
import { useQueryParams } from "../../hooks/useQueryParams";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaNewspaper } from "react-icons/fa";
import { Pagination } from "../common/pagination/Pagination";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";

type Props = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListArticle = ({ onCreate, onUpdate }: Props) => {
  const dispatch = useAppDispatch();
  const { articles, pagination, loading } = useAppSelector(
    (state) => state.article,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const isPublished = getParam("isPublished", "all");
  const search = getParam("search", "");

  const handleChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parsedIsPublished =
    isPublished === "all" ? undefined : isPublished === "true" ? true : false;

  useEffect(() => {
    dispatch(
      getArticles({ page, limit, isPublished: parsedIsPublished, search }),
    );
  }, [dispatch, page, limit, parsedIsPublished, search]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deleteArticle(confirmDeleteId)).unwrap();
      toast.success("Xóa bài viết thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý bài viết"
        subtitle={pagination ? `Tổng ${pagination.total} bài viết` : undefined}
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm bài viết
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm bài viết theo tiêu đề..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <select
              name="isPublished"
              value={isPublished}
              onChange={handleChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="true">Đã xuất bản</option>
              <option value="false">Bản nháp</option>
            </select>
            <select
              name="limit"
              value={limit}
              onChange={handleChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        <DataTable
          data={articles}
          rowKey={(a) => a._id}
          loading={loading.list}
          columns={[
            {
              key: "thumbnail",
              header: "Ảnh",
              align: "center",
              width: "100px",
              render: (a) => (
                <img
                  src={a.thumbnail?.url}
                  alt={a.title}
                  className="h-12 w-16 border border-gray-200 rounded-lg object-cover mx-auto"
                />
              ),
            },
            {
              key: "title",
              header: "Tiêu đề",
              align: "left",
              render: (a) => (
                <span className="font-medium text-gray-900 line-clamp-2">
                  {a.title}
                </span>
              ),
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (a) =>
                a.isPublished ? (
                  <StatusBadge tone="success" label="Đã xuất bản" />
                ) : (
                  <StatusBadge tone="warning" label="Bản nháp" />
                ),
            },
            {
              key: "view",
              header: "Lượt xem",
              align: "center",
              width: "100px",
              render: (a) => a.viewCount,
            },
            {
              key: "created",
              header: "Ngày tạo",
              align: "center",
              width: "120px",
              render: (a) => formatDate(a.createdAt),
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (a) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(a._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmDeleteId(a._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaNewspaper />}
              title={
                search ? "Không tìm thấy bài viết nào" : "Chưa có bài viết"
              }
              description={
                search
                  ? "Thử tìm với từ khóa khác"
                  : "Bắt đầu viết bài đầu tiên"
              }
              action={
                !search ? (
                  <button
                    type="button"
                    onClick={onCreate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                  >
                    + Thêm bài viết
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa bài viết này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
